import { useCallback } from 'react';
import { StageGroup, StageInfo } from '../types/stageTypes';
import { DefaultStageMappingType } from '../../../api/integrations';
import { applyTypeToStages, toggleStageUnmappedState, calculateChangeCounts } from '../utils/stageModification';
import { groupStagesByType } from '../utils/stageGrouping';

/**
 * Custom hook for managing stage modifications
 * @param stageGroups Current stage groups
 * @param originalStages Original stage data
 * @param selectedStages Set of selected stage IDs
 * @param setOriginalStages Function to update original stages
 * @param setGroups Function to update stage groups
 * @param clearSelection Function to clear selection
 * @returns Functions and state for stage modifications
 */
export const useStageModification = (
    stageGroups: StageGroup[],
    originalStages: StageInfo[],
    selectedStages: Set<string>,
    setOriginalStages: (stages: StageInfo[]) => void,
    setGroups: (groups: StageGroup[]) => void,
    clearSelection: () => void
) => {
    // Apply stage type to selected stages
    const applyStageTypeToSelected = useCallback((
        stageType: DefaultStageMappingType | null,
        isUnmapped: boolean = false
    ) => {
        // Skip if no stages are selected
        if (selectedStages.size === 0) return;

        // Apply the stage type to all selected stages
        const updatedStages = applyTypeToStages(
            stageGroups,
            originalStages,
            selectedStages,
            stageType,
            isUnmapped
        );

        // Update original stages
        setOriginalStages(updatedStages);

        // Update groups
        setGroups(groupStagesByType(updatedStages));

        // Clear selection after applying
        clearSelection();
    }, [stageGroups, originalStages, selectedStages, setOriginalStages, setGroups, clearSelection]);

    // Toggle "always unmap" state for a stage
    const toggleAlwaysUnmap = useCallback((stageId: string, unmapped: boolean) => {
        const updatedStages = toggleStageUnmappedState(
            stageGroups,
            originalStages,
            stageId,
            unmapped
        );

        // Update original stages
        setOriginalStages(updatedStages);

        // Update groups
        setGroups(groupStagesByType(updatedStages));
    }, [stageGroups, originalStages, setOriginalStages, setGroups]);

    // Calculate counts for changes
    const { addedCount, updatedCount, unmappedCount } = calculateChangeCounts(stageGroups);

    return {
        applyStageTypeToSelected,
        toggleAlwaysUnmap,
        addedCount,
        updatedCount,
        unmappedCount
    };
}; 